/**
 * FASE 59 — dominio de MODELO (Sonnet 5 / Opus 4.8 / Fable 5 / Opus 5) por sessao/terminal.
 *
 * O usuario opera varias familias no provider `claude`: Sonnet 5 (custo-beneficio), Opus 4.8 e Fable 5
 * (legado), e Opus 5 (FASE 197 — o modelo mais superior da claude, novo default do master; ja reconhecido
 * pela CLI claude 2.1.219 como `claude-opus-5`). O term-deck (DONO/launcher das sessoes) GERE o modelo
 * por cwd — exibe qual modelo cada sessao usa, permite override por projeto e ALINHA o modelo na
 * retomada, evitando que uma sessao com cache construido em X seja retomada em Y (o que reprocessaria o
 * historico — a mesma dor ja resolvida para o EFFORT na FASE 30).
 *
 * >>> DIFERENCA ARQUITETURAL vs EFFORT (achado do double-check + de-risk empirico, 2026-06-30):
 *  - O MODELO FICA GRAVADO no rollout.jsonl de cada sessao (`message.model`, ex: `claude-opus-4-8`),
 *    ao contrario do effort (que NAO fica no rollout). Confirmado lendo rollouts reais.
 *  - NAO HA env de TRAVA para o modelo. `CLAUDE_CODE_EFFORT_LEVEL` faz o `/effort` interativo RECUSAR;
 *    ja `ANTHROPIC_MODEL`/`--model` (que controlam o modelo de SPAWN) NAO impedem o `/model` interativo
 *    de trocar dentro da sessao. Logo o portal CONTROLA o modelo de SPAWN (sessao nova/retomada) mas
 *    NAO pode "travar" uma sessao ja aberta — o fluxo de troca segura (59.6) e ATIVO, nao uma trava.
 *
 * >>> DE-RISK EMPIRICO (59.1a, read-only sobre rollouts reais, 2026-06-30 — zero cota, sem tocar a frota):
 *  - Sessoes vivas/continuadas RETEM o modelo do launch mesmo quando o default GLOBAL muda: com o
 *    settings.json `model: sonnet`, os rollouts de Auto-TradingView e CoteCarbono permaneceram
 *    `claude-opus-4-8` o dia inteiro (o de Auto-TradingView foi CRIADO hoje 03:32, ~13s apos o anterior
 *    terminar — assinatura de `--continue` — e nasceu/seguiu opus, nao snapou para o sonnet default).
 *    O modelo so muda numa fronteira de RE-LAUNCH/troca de provider, nunca espontaneamente para o novo
 *    default. Nao foi possivel isolar 100% o caso puro claude->claude `--continue`-com-default-trocado do
 *    comportamento de processo longo (a frota viva sao PTYs long-running via send_input, nao re-launch);
 *    o teste tatil isolado exigiria um launch interativo real = cota + risco de interferir na frota.
 *  - DECISAO CONSERVADORA (segura nos DOIS cenarios): implementar `decideLaunchModel` COM auto-align na
 *    retomada (espelha o effort). Se o Claude Code preservar o modelo do rollout no `--continue`, injetar
 *    o mesmo modelo registrado e um no-op inofensivo (coincide); se ele usar o default/env vigente,
 *    injetar o registrado e ESSENCIAL para nao trocar de modelo por acidente e reprocessar o historico
 *    (o custo exato que o usuario apontou). Auto-align NUNCA piora e protege o cache no caso incerto.
 *
 * Modulo PURO (sem IO): a leitura do default vigente vive no server (DefaultModelReader) e a persistencia
 * por cwd no ProjectModelStore; aqui ficam os TIPOS + as POLITICAS testaveis.
 */
import { z } from 'zod';
/**
 * IDs canonicos dos modelos que o term-deck gerencia. Fonte de verdade: o system prompt do Claude Code
 * (Model IDs atuais) + confirmado nos rollouts reais (`message.model`). Sonnet 5 = padrao (custo-beneficio),
 * Opus 4.8 = altissima complexidade legada, Opus 5 = modelo mais superior da claude (FASE 197 — novo default
 * do master, ja reconhecido pela CLI claude 2.1.219). NAO usar nomes desatualizados (ex: "claude-opus-4-6")
 * que pesquisa externa citou — os IDs abaixo sao os corretos.
 */
// M488.12b — `claude-fable-5-1` entrou pelo caminho novo (M488.12/.12c): uma linha aqui, uma no rótulo e
// uma no preço. O alias `fable` passa a resolver para ele SOZINHO, porque agora é derivado da família.
export const MODEL_IDS = [
    'claude-sonnet-5',
    // 29/09/2026 — Sonnet 5.5 (pedido do dono para uma Operação do Agon). Pela regra de família, `sonnet` passa a abrir nele.
    'claude-sonnet-5-5',
    'claude-opus-4-8',
    'claude-opus-5',
    // 22/09/2026 — Opus 5.5 (o CLI 2.1.280 já resolve `opus` para ele). Superou o Opus 5 pela regra de família:
    // nenhuma referência gravada precisa ser reescrita para o launch passar a abrir nele (ver model-lifecycle.ts).
    'claude-opus-5-5',
    'claude-fable-5',
    'claude-fable-5-1',
];
export const modelIdSchema = z.enum(MODEL_IDS);
/** Type guard para um ModelId vindo de fonte nao-tipada (SQLite/JSON/env/settings). */
export function isModelId(v) {
    return typeof v === 'string' && MODEL_IDS.includes(v);
}
/** Rotulo curto e legivel de um modelo (o chip/tooltip da UI usa isto). */
export const MODEL_LABELS = {
    'claude-sonnet-5': 'Sonnet 5',
    'claude-sonnet-5-5': 'Sonnet 5.5',
    'claude-opus-4-8': 'Opus 4.8',
    'claude-opus-5': 'Opus 5',
    'claude-opus-5-5': 'Opus 5.5',
    'claude-fable-5': 'Fable 5',
    'claude-fable-5-1': 'Fable 5.1',
};
const FAMILY_PREFIX = {
    sonnet: 'claude-sonnet-',
    opus: 'claude-opus-',
    fable: 'claude-fable-',
};
/** `claude-fable-5-1` -> [5, 1]; `claude-opus-4-8` -> [4, 8]. Segmento não numérico encerra a versão. */
function versionOf(id, prefix) {
    const rest = id.slice(prefix.length).split('-');
    const parts = [];
    for (const segment of rest) {
        const n = Number.parseInt(segment, 10);
        if (!Number.isFinite(n) || `${n}` !== segment)
            break;
        parts.push(n);
    }
    return parts;
}
function newer(a, b) {
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i += 1) {
        const left = a[i] ?? 0;
        const right = b[i] ?? 0;
        if (left !== right)
            return left > right;
    }
    return false;
}
/**
 * AL.E5 (AF.27, 20/09/2026) — um modelo esta SUPERADO quando existe um mais novo da mesma familia no catalogo
 * (`claude-opus-4-8` -> Opus 5; `claude-fable-5` -> Fable 5.1). O id continua valido (sessoes/registros antigos
 * o citam), mas a RETOMADA nunca o ressuscita: `decideLaunchModel` troca o registro superado pelo atual.
 */
export function familyOf(id) {
    for (const family of Object.keys(FAMILY_PREFIX)) {
        if (id.startsWith(FAMILY_PREFIX[family]))
            return family;
    }
    return null;
}
/**
 * 01/10/2026 — família LEGADA: superada pelo modelo vigente de OUTRA família, em toda abertura e retomada. Decisão do
 * dono (Comandos_times/orchestration/user_input_raw.md, 01/10 20:11): "Fable 5.1 se tornou um modelo legado … extremamente
 * caro … não compensa mais utilizar esse modelo"; o Opus 5.5 é superior e mais barato. Exceção só por pin do dono
 * (`set_model_version_pin`, human_only). O id continua válido para ler registros antigos.
 */
export const LEGACY_MODEL_FAMILIES = { fable: 'opus' };
export function isLegacyFamilyModel(id) {
    const family = familyOf(id);
    return family !== null && LEGACY_MODEL_FAMILIES[family] !== undefined;
}
export function isSupersededModel(id) {
    const family = familyOf(id);
    if (!family)
        return false;
    if (LEGACY_MODEL_FAMILIES[family])
        return true;
    const latest = latestModelOfFamily(family);
    return latest !== null && latest !== id;
}
/** O modelo ATUAL para um id (o proprio, o mais novo da familia quando superado, ou o vigente da família sucessora). */
export function currentModelFor(id) {
    const family = familyOf(id);
    const successor = family ? LEGACY_MODEL_FAMILIES[family] : undefined;
    if (successor)
        return latestModelOfFamily(successor) ?? id;
    return (family && latestModelOfFamily(family)) || id;
}
/** O modelo mais recente de uma família entre os que o term-deck gere, ou `null` se não houver nenhum. */
export function latestModelOfFamily(family) {
    const prefix = FAMILY_PREFIX[family];
    let best = null;
    let bestVersion = [];
    for (const id of MODEL_IDS) {
        if (!id.startsWith(prefix))
            continue;
        const version = versionOf(id, prefix);
        if (best === null || newer(version, bestVersion)) {
            best = id;
            bestVersion = version;
        }
    }
    return best;
}
function buildModelAliases() {
    const aliases = {};
    // Todo id canônico é alias de si mesmo (passthrough) — nasce da lista, nunca de digitação.
    for (const id of MODEL_IDS)
        aliases[id] = id;
    // Alias curto por família = o mais recente, espelhando a CLI.
    for (const family of Object.keys(FAMILY_PREFIX)) {
        const latest = latestModelOfFamily(family);
        if (latest)
            aliases[family] = latest;
    }
    // Alias EXPLÍCITO por versão, para quem quer um modelo legado por extenso (`opus-4-8`, `fable-5`).
    for (const id of MODEL_IDS) {
        const short = id.replace(/^claude-/u, '');
        if (!aliases[short])
            aliases[short] = id;
    }
    return aliases;
}
const MODEL_ALIASES = buildModelAliases();
/**
 * 22/09/2026 — o Opus VIGENTE, derivado do catálogo. Política que diz "Opus/alto" (MASTER, runners, fallback do
 * Fable, categoria Workana, presets) referencia ESTA constante, nunca um id literal: o próximo Opus entra por
 * uma linha em `MODEL_IDS` e toda política passa a abri-lo. Id literal de Opus fora do catálogo é recusado
 * pelo contrato `model-literal-contract.test.ts`.
 */
export const CURRENT_OPUS_MODEL = latestModelOfFamily('opus') ?? 'claude-opus-5';
/** Sonnet VIGENTE, derivado do catálogo como o Opus: padrão de sessão Claude de projeto (política por risco, 08/10). */
export const CURRENT_SONNET_MODEL = latestModelOfFamily('sonnet') ?? 'claude-sonnet-5';
/** Sufixo de variante de contexto do Claude Code (`[1m]`). */
const CONTEXT_VARIANT = /\[[^\]]*\]$/u;
/**
 * Normaliza um valor cru de modelo (alias curto OU ID canonico OU ID versionado tipo `claude-sonnet-5-...`
 * OU variante de contexto tipo `claude-fable-5[1m]`/`opus[1m]`) para um ModelId gerenciado, ou `null` se
 * nao for um dos modelos que gerimos (ex: haiku, MiniMax, modelo de wrapper) — nesse caso o term-deck nao
 * gerencia (mostra "padrao"). FASE 61: Fable 5 (novo default global do usuario) agora e gerido. FASE 197:
 * Opus 5 tambem e gerido — a familia `/opus-?5/` casa ANTES da familia generica `/opus/` (que agora e o
 * fallback do Opus MAIS RECENTE, ja que `opus` sozinho = Opus 5 na CLI).
 */
export function normalizeModel(raw) {
    if (typeof raw !== 'string')
        return null;
    // Variante de contexto (`[1m]`) não muda o modelo: sai antes de qualquer comparação.
    const v = raw.trim().toLowerCase().replace(CONTEXT_VARIANT, '');
    if (v.length === 0)
        return null;
    if (MODEL_ALIASES[v])
        return MODEL_ALIASES[v];
    // 22/09/2026 — a leitura por regex fixa (`/opus-?5/ -> claude-opus-5`) lia `claude-opus-5-5` como Opus 5:
    // o modelo novo ficava invisível exatamente onde a sessão o anunciava. Agora a versão é DERIVADA do catálogo.
    // 1) id versionado com sufixo (snapshot/data): o MAIOR id do catálogo que é prefixo em fronteira de segmento.
    const full = v.startsWith('claude-') ? v : `claude-${v}`;
    let best = null;
    for (const id of MODEL_IDS) {
        if ((full === id || full.startsWith(`${id}-`)) && (!best || id.length > best.length))
            best = id;
    }
    if (best)
        return best;
    // 2) forma livre da família (`opus5`, `claude-opus-4-7`): versão exata do catálogo; mesma major; senão o
    // mais recente da família quando nenhuma versão foi dita. Versão maior que qualquer catalogada NÃO vira a
    // antiga — devolve null e o detector de catálogo (model-lifecycle) acusa o modelo novo.
    for (const family of Object.keys(FAMILY_PREFIX)) {
        if (!v.includes(family))
            continue;
        const match = v.match(new RegExp(`${family}-?(\\d+)(?:[-.](\\d+))?`, 'u'));
        if (!match)
            return latestModelOfFamily(family);
        const wanted = [Number(match[1]), ...(match[2] !== undefined ? [Number(match[2])] : [])];
        const ids = MODEL_IDS.filter((id) => id.startsWith(FAMILY_PREFIX[family]));
        const exact = ids.find((id) => versionOf(id, FAMILY_PREFIX[family]).join('.') === wanted.join('.'));
        if (exact)
            return exact;
        const sameMajor = ids.filter((id) => versionOf(id, FAMILY_PREFIX[family])[0] === wanted[0]);
        if (sameMajor.length > 0) {
            return sameMajor.reduce((a, b) => (newer(versionOf(b, FAMILY_PREFIX[family]), versionOf(a, FAMILY_PREFIX[family])) ? b : a));
        }
        const latest = latestModelOfFamily(family);
        if (latest && newer(wanted, versionOf(latest, FAMILY_PREFIX[family])))
            return null;
        return latest;
    }
    return null;
}
/**
 * Valor a INJETAR no launch (env `ANTHROPIC_MODEL` / flag `--model`) — ALIAS curto (`sonnet`/`fable`) ou
 * ID CANONICO COMPLETO (`claude-opus-4-8`/`claude-opus-5`), garantidamente reconhecido pela CLI.
 * Deterministico por ModelId. NAO carrega variante de contexto (`[1m]`) — para preservar a variante,
 * usar `modelInjectionValue` (FASE 61.4).
 *
 * FASE 197 — CORRECAO CRITICA: Opus 4.8 NAO PODE mais injetar o alias curto `opus`, porque na CLI 2.1.219
 * esse alias resolve para Opus 5 — injetar `opus` para uma sessao que deveria abrir em Opus 4.8 subiria o
 * modelo ERRADO silenciosamente. Por isso opus-4-8 injeta o ID completo `claude-opus-4-8` (a CLI aceita
 * nome completo via --model/ANTHROPIC_MODEL). Opus 5 injeta o proprio ID completo `claude-opus-5` (mais
 * explicito que depender do alias `opus`, que pode mudar de significado de novo no futuro).
 */
export function modelCliValue(model) {
    // 22/09/2026 — ID completo sempre (salvo o `sonnet` histórico, enquanto for o mais novo da família). Alias
    // curto é resolvido pelo BINÁRIO: um terminal num CLI ainda não reiniciado (2.1.278, "Update installed")
    // resolvia `opus` para Opus 5 depois do lançamento do 5.5. O id explícito independe da versão do binário.
    if (model === 'claude-sonnet-5' && latestModelOfFamily('sonnet') === model)
        return 'sonnet';
    return model;
}
/**
 * FASE 61.4 — valor a INJETAR em `ANTHROPIC_MODEL` PRESERVANDO a variante de contexto (ex: `[1m]`).
 *
 * Problema: o usuario roda com `model: "claude-fable-5[1m]"` no settings.json (contexto 1M). Injetar o
 * alias curto (`fable`) no launch DERRUBARIA o `[1m]` — a sessao perderia o contexto 1M. Isso vale tanto
 * para o caminho de default quanto para o OVERRIDE explicito do seletor da UI (que envia um ModelId sem
 * variante). De-risk empirico (2026-07-01): `ANTHROPIC_MODEL="claude-fable-5[1m]"` E ACEITO pela CLI.
 *
 * Estrategia: quando o valor RAW do default (com a variante) normaliza para a MESMA familia do modelo
 * decidido, injeta o RAW (preserva a variante). Caso contrario (modelo decidido != familia do default,
 * ex: o usuario escolheu Opus enquanto o default e Fable), injeta o alias curto canonico. `rawDefault`
 * vem do settings do usuario (DefaultModelReader.readRaw).
 */
export function modelInjectionValue(model, rawDefault) {
    // A variante (`[1m]`) pertence à FAMÍLIA configurada pelo dono, não a uma versão: `opus[1m]` no settings
    // continua valendo quando o launch decide o Opus 5.5 — mas com o id explícito, nunca o alias do binário.
    const raw = rawDefault?.trim() ?? '';
    const variant = raw.match(CONTEXT_VARIANT)?.[0] ?? '';
    const rawModel = normalizeModel(raw);
    if (variant && rawModel && familyOf(rawModel) !== null && familyOf(rawModel) === familyOf(model)) {
        return `${modelCliValue(model)}${variant}`;
    }
    return modelCliValue(model);
}
/**
 * O launch deste provider/kind suporta gestao de modelo pelo term-deck? Mesma logica de N/A do effort
 * (providerSupportsEffort): so o binario claude.exe contra um modelo ANTHROPIC.
 *  - terminal + `claude`.
 *  - orquestrador + `claude` (claude-based).
 * N/A: terminal `codex` (modelo proprio do codex — ver `providerSupportsCodexModel` em codex-model.ts) e
 * wrappers minimax/mimo/mimo pro/glm (backend NAO-Anthropic — o modelo do claude e irrelevante/definido
 * pelo proprio wrapper).
 *
 * FASE 149 (149.2) — CORRECAO: ate a FASE 148, `orquestrador + codex` retornava `true` porque
 * `resolveOrchestrator` DEGRADAVA codex para claude.exe (o modelo Anthropic decidido de fato era aplicado
 * ao processo claude.exe real que subia). A FASE 149.2 destravou o codex como orquestrador REAL (nao
 * degrada mais) — o dominio de modelo relevante passou a ser o do Codex (`providerSupportsCodexModel`,
 * SEMPRE true p/ orquestrador tambem), entao este dominio Anthropic-especifico NAO se aplica mais a
 * `codex` em nenhum kind.
 *
 * FASE 125.A (125.A.4) — `grok` PERMANECE false por DECISAO EXPLICITA (nao omissao): o dominio "modelo"
 * do term-deck e sobre qual modelo ANTHROPIC o claude.exe roda (Sonnet/Opus/Fable). O Grok Build tem seu
 * proprio espaco de modelos (grok-4.5 default, grok-composer-2.5-fast), gerido por `grok --model`/`grok
 * models`, fora dessa abstracao. Injetar ANTHROPIC_MODEL num grok seria inerte/errado.
 */
export function providerSupportsModel(provider, kind = 'terminal') {
    if (kind === 'orchestrator')
        return provider === 'claude';
    return provider === 'claude';
}
/**
 * POLITICA de modelo no launch (59.1 — decisao conservadora com auto-align, ver cabecalho). Retorna o
 * ModelId a aplicar, ou `null` quando nao se aplica / nao da para determinar (o resolver entao DELETA a
 * env ANTHROPIC_MODEL e o claude usa seu proprio default do settings.json).
 *
 * Precedencia (identica a decideLaunchEffort, + a excecao 3a da FASE 125.B.2):
 *  1. provider/kind nao suporta modelo -> `null` (N/A).
 *  2. override explicito do seletor -> vence (escolha deliberada; possivel relaunch com custo).
 *  3a. RETOMADA + PREFERIDO mais recente que o REGISTRADO -> usa o PREFERIDO (FASE 125.B.2: intencao
 *     explicita mais recente do usuario vence o auto-align; corrige o cold-open do backlogSweep que
 *     ignorava a preferencia nova de um projeto com sessao anterior). So com AMBOS os timestamps.
 *  3b. RETOMADA -> auto-align ao modelo REGISTRADO (protege o cache). Sem registrado (sessao
 *     PRE-EXISTENTE) -> cai no default vigente.
 *  4. sessao NOVA -> modelo PREFERIDO do projeto, senao o default vigente.
 */
export function decideLaunchModel(input) {
    if (!providerSupportsModel(input.provider, input.kind ?? 'terminal'))
        return null;
    if (input.explicitOverride)
        return input.explicitOverride;
    if (input.resuming) {
        // FASE 125 (125.B.2) — o usuario mudou o PREFERIDO DEPOIS do ultimo registro? Sua intencao explicita
        // mais recente vence a protecao de cache. Exige os DOIS timestamps + preferido presente (sem eles, ou
        // com registeredAt >= preferredAt, cai no auto-align classico — comportamento 100% preservado).
        if (input.preferred &&
            input.preferredAt != null &&
            input.registeredAt != null &&
            input.preferredAt > input.registeredAt) {
            return input.preferred;
        }
        // AL.E5 (AF.27) — registro de modelo SUPERADO (ex.: `claude-opus-4-8` em 30+ projetos) nao volta na
        // retomada: o cache do prompt ja nao existe para um modelo que a familia superou, e reabrir nele e regressao.
        const registered = input.registered && isSupersededModel(input.registered) ? currentModelFor(input.registered) : input.registered;
        return registered ?? input.defaultModel ?? null;
    }
    return input.preferred ?? input.defaultModel ?? null;
}
/**
 * Nome da variavel de ambiente que o binario do Claude Code le para o modelo primario. Injetada por
 * spawn (mesmo mecanismo do EFFORT_ENV_VAR). DIFERENTE do effort: NAO trava o `/model` interativo — so
 * define o modelo de SPAWN. Herdada por todos os caminhos de launch (bootstrap, orquestrador, wrappers).
 */
export const MODEL_ENV_VAR = 'ANTHROPIC_MODEL';
/**
 * FASE 135 (135.D.4) — CONFIG de MODELO do master + defaults por TIPO de runner (aba de gestao + REST/MCP).
 *
 * `masterModel`: modelo com que o MASTER (kind=orchestrator, master=true) e ABERTO. Default `Opus 5` (FASE
 * 197, mandato do usuario — Opus 5 e o modelo mais superior da claude; Fable 5 vira excecao configuravel).
 * Aplica no PROXIMO open do master (padrao 59.6 — SEM troca a quente; o openMaster o passa como override
 * explicito). Antes, o master herdava mudo o default do ambiente.
 *
 * `runnerDefaults`: modelo default por PAPEL de runner (judge/triage/auditor/task). Default `Sonnet 5`
 * CONSERVADOR (contra-argumento registrado: Grok 4.5 nao e default ate a validacao tatil 125.A.7 + benchmark
 * 132.A.4; a config nasce junto e o usuario troca na UI). APLICA-SE aos runners claude-based (judge/auditor/
 * task); `triage` roda em minimax (wrapper, providerSupportsModel=false) — o campo e informativo/futuro.
 */
export const RUNNER_MODEL_ROLES = ['judge', 'triage', 'auditor', 'task'];
const RUNNER_MODEL_ROLE_SET = new Set(RUNNER_MODEL_ROLES);
export function isRunnerModelRole(v) {
    return RUNNER_MODEL_ROLE_SET.has(v);
}
/** Default do MASTER = Opus 5 (FASE 197, mandato do usuario — Fable 5 vira excecao). */
export const DEFAULT_MASTER_MODEL = CURRENT_OPUS_MODEL;
/**
 * Default dos RUNNERS = Sonnet vigente (dono, 31/10, ed3018fe/#537: "nao pode mais deixar abrir em opus, a menos com
 * justificativa explicita"). Opus num runner e escolha gravada (`set_runner_model`), nunca o default de fabrica.
 */
export const DEFAULT_RUNNER_MODEL = CURRENT_SONNET_MODEL;
/** M291 — defaults de fabrica dos runners (todos Sonnet vigente desde 31/10, ed3018fe). */
export function defaultRunnerModelDefaults() {
    return { judge: DEFAULT_RUNNER_MODEL, triage: DEFAULT_RUNNER_MODEL, auditor: DEFAULT_RUNNER_MODEL, task: DEFAULT_RUNNER_MODEL };
}
/** Normaliza o `masterModel` de fonte nao-tipada (JSON/env). Invalido/ausente -> default (Fable). BC-safe. */
export function parseMasterModel(v) {
    return normalizeModel(v) ?? DEFAULT_MASTER_MODEL;
}
/** Normaliza `runnerDefaults` (por papel) de fonte nao-tipada. Papel ausente/invalido -> default (Sonnet 5). BC-safe. */
export function parseRunnerModelDefaults(v) {
    const out = defaultRunnerModelDefaults();
    if (typeof v === 'object' && v !== null) {
        const o = v;
        for (const role of RUNNER_MODEL_ROLES) {
            const m = normalizeModel(o[role]);
            if (m)
                out[role] = m;
        }
    }
    return out;
}
//# sourceMappingURL=model.js.map